import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import {
  FileText, CalendarClock, Search, Users, GraduationCap, FolderOpen,
  BookOpen, Mic, MapPin, HelpCircle, Compass, ArrowRight, Sparkles, Scale,
  MapPinned, Inbox, Footprints, AlarmClock, ClipboardCheck, ShieldCheck,
  FolderKanban, Video, History, ClipboardList, FileEdit,
} from "lucide-react";

const TASKS = [
  { to: "/summarize", key: "task.court_papers", label: "I got court papers", desc: "Paste them here for a plain-English summary.", esLabel: "Recibí papeles de la corte", esDesc: "Pégalos aquí para un resumen en palabras sencillas.", Icon: FileText, testid: "task-court-papers" },
  { to: "/my-case", key: "task.court_date", label: "I have a court date", desc: "Build your prep checklist and timeline.", esLabel: "Tengo una fecha de corte", esDesc: "Arma tu lista de preparación y tu línea de tiempo.", Icon: CalendarClock, testid: "task-court-date" },
  { to: "/summarize", key: "task.understand_case", label: "I need to understand my case", desc: "Explain a document, notice, or order.", esLabel: "Necesito entender mi caso", esDesc: "Explica un documento, aviso u orden.", Icon: Search, testid: "task-understand-case" },
  { to: "/resources", key: "task.find_lawyer", label: "I need to find a lawyer", desc: "Find legal aid and self-help centers near you.", esLabel: "Necesito encontrar un abogado", esDesc: "Encuentra ayuda legal y centros de autoayuda cerca de ti.", Icon: Users, testid: "task-find-lawyer" },
  { to: "/learn", key: "task.learn", label: "I want to learn about court", desc: "Short lessons in plain English.", esLabel: "Quiero aprender sobre la corte", esDesc: "Lecciones cortas en palabras sencillas.", Icon: GraduationCap, testid: "task-learn" },
  { to: "/my-case", key: "task.organize", label: "I need help organizing", desc: "Build a personal case timeline.", esLabel: "Necesito ayuda organizando", esDesc: "Arma una línea de tiempo personal del caso.", Icon: FolderOpen, testid: "task-organize" },
  { to: "/glossary", key: "task.term", label: "I need to understand a legal term", desc: "Search 25+ legal words in plain English.", esLabel: "Necesito entender un término legal", esDesc: "Busca más de 25 palabras legales en lenguaje sencillo.", Icon: BookOpen, testid: "task-term" },
  { to: "/practice", key: "task.practice", label: "I need to practice talking in court", desc: "Rehearse respectful, clear phrases.", esLabel: "Necesito practicar hablar en la corte", esDesc: "Ensaya frases respetuosas y claras.", Icon: Mic, testid: "task-practice" },
  { to: "/case-law", key: "task.case_law", label: "I want to search case opinions", desc: "AI overview + links to free case-law databases.", esLabel: "Quiero buscar opiniones judiciales", esDesc: "Resumen con IA y enlaces a bases de datos gratuitas.", Icon: Scale, testid: "task-case-law" },
  { to: "/waiver", key: "task.waiver", label: "I need a court fee waiver", desc: "Draft a fee-waiver application to print.", esLabel: "Necesito una exención de tarifas de corte", esDesc: "Redacta una solicitud de exención para imprimir.", Icon: FileText, testid: "task-waiver" },
  { to: "/resources", key: "task.resource", label: "I need to find a court resource", desc: "Illinois legal-aid and clerks directory.", esLabel: "Necesito encontrar un recurso de la corte", esDesc: "Directorio de ayuda legal y secretarías de Illinois.", Icon: MapPin, testid: "task-resource" },
  { to: "/what-if", key: "task.what_if", label: "Something happened — what do I do next?", desc: "Common situations and next steps.", esLabel: "Pasó algo — ¿qué hago ahora?", esDesc: "Situaciones comunes y próximos pasos.", Icon: HelpCircle, testid: "task-what-if" },
  { to: "/research", key: "task.research", label: "Research the rule that applies", desc: "Get the statute, court rule, or case that answers your question — with the source cited.", esLabel: "Investigar la regla que aplica", esDesc: "Encuentra el estatuto, regla de corte o caso que responde tu pregunta — con la fuente citada.", Icon: Scale, testid: "task-research" },
  { to: "/builder", key: "task.builder", label: "Build a court document draft", desc: "Timelines, motions, declarations, discovery — all labeled DRAFT.", esLabel: "Construir un borrador para la corte", esDesc: "Cronologías, mociones, declaraciones, descubrimiento — todo etiquetado como BORRADOR.", Icon: FolderKanban, testid: "task-builder" },
  { to: "/sessions", key: "task.sessions", label: "Meet with a Community Court Navigator", desc: "Book a 30-minute virtual session with a human navigator.", esLabel: "Reunirse con una Navegadora Comunitaria", esDesc: "Reserva una sesión virtual de 30 minutos con una navegadora humana.", Icon: Video, testid: "task-sessions" },
  { to: "/court-ready", key: "task.court_ready_book", label: "Read the Court Ready book", desc: "Nina Watts' full Illinois civil-court guide — Court Ready+ members.", esLabel: "Lee el libro Court Ready", esDesc: "La guía completa de Nina Watts sobre la corte civil de Illinois — miembros Court Ready+.", Icon: BookOpen, testid: "task-court-ready-book" },
];

export default function Home() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [nextEvent, setNextEvent] = useState(null);
  const [recent, setRecent] = useState([]);
  const tasks = TASKS.map((x) => ({
    ...x,
    displayLabel: lang === "es" ? x.esLabel : x.label,
    displayDesc: lang === "es" ? x.esDesc : x.desc,
  }));

  useEffect(() => {
    if (!user) { setNextEvent(null); setRecent([]); return; }
    let cancelled = false;
    api.get("/case/state").then((r) => {
      if (cancelled) return;
      const today = new Date().toISOString().slice(0, 10);
      const upcoming = (r.data?.events || [])
        .filter((e) => e.date >= today)
        .sort((a, b) => a.date.localeCompare(b.date));
      setNextEvent(upcoming[0] || null);
    }).catch(() => setNextEvent(null));
    api.get("/me/recent").then((r) => {
      if (cancelled) return;
      setRecent(r.data?.items || []);
    }).catch(() => setRecent([]));
    return () => { cancelled = true; };
  }, [user]);

  const nextDateFmt = nextEvent
    ? new Date(nextEvent.date + "T12:00:00").toLocaleDateString(lang === "es" ? "es-US" : "en-US", { year: "numeric", month: "short", day: "numeric" })
    : null;
  return (
    <div className="space-y-8">
      <section
        data-testid="hero"
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#C85A32] to-[#9E3F1E] text-white p-6 sm:p-10 shadow-xl border border-[#B24C28]/30"
      >
        <div className="absolute inset-0 pod-grain opacity-30 pointer-events-none" />
        <div className="relative max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full text-xs uppercase tracking-widest font-semibold mb-4">
            <Sparkles className="w-3.5 h-3.5" /> {t("home.hero_pill")}
          </div>
          <h1 className="font-extrabold leading-tight tracking-tight" data-testid="hero-title">
            <span className="block text-3xl sm:text-5xl lg:text-6xl">{t("home.hero_title_1")}</span>
            <span className="block mt-1 text-xl sm:text-3xl lg:text-4xl">{t("home.hero_title_2")}</span>
            <span className="block mt-1 text-sm sm:text-lg lg:text-2xl">{t("home.hero_title_3")}</span>
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-white/90 max-w-2xl">
            {t("home.hero_sub")}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/summarize"
              data-testid="cta-i-got-served"
              className="pod-focus w-full sm:w-auto bg-white text-[#C85A32] hover:bg-[#FFF8F5] text-lg sm:text-xl font-extrabold py-4 px-6 rounded-2xl shadow-lg transition-all inline-flex items-center justify-center gap-3 border-2 border-white/90"
            >
              <Inbox className="w-5 h-5" />
              {t("home.cta_start")}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/navigator"
              data-testid="cta-meet-navigator"
              className="pod-focus w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white text-base font-bold py-4 px-6 rounded-2xl border border-white/40 transition-colors inline-flex items-center justify-center gap-2"
            >
              {t("home.cta_ask_deborah")}
            </Link>
          </div>
          <div className="mt-6 text-xs uppercase tracking-[0.2em] font-bold text-white/70" data-testid="hero-tagline">
            {t("home.tagline_full")}
          </div>
        </div>
      </section>

      {/* What Happens Next? — six-step journey card */}
      <section
        data-testid="what-happens-next"
        className="relative overflow-hidden rounded-3xl bg-[#2C4A3E] text-white p-6 sm:p-10 shadow-lg"
      >
        <div className="absolute inset-0 pod-grain opacity-20 pointer-events-none" />
        <div className="relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full text-[11px] uppercase tracking-widest font-bold" data-testid="whn-kicker">
                <MapPinned className="w-3.5 h-3.5" /> {t("home.whn_kicker")}
              </div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">{t("home.whn_title")}</h2>
              <p className="mt-2 text-white/85 max-w-2xl">{t("home.whn_sub")}</p>
            </div>
            <Link
              to="/my-case"
              data-testid="whn-cta"
              className="pod-focus shrink-0 bg-white text-[#2C4A3E] hover:bg-[#F5EFDD] font-bold py-3 px-5 rounded-xl inline-flex items-center gap-2"
            >
              {t("home.whn_cta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <ol className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { n: "1", Icon: MapPinned,     tKey: "home.whn_step1" },
              { n: "2", Icon: Inbox,         tKey: "home.whn_step2" },
              { n: "3", Icon: Footprints,    tKey: "home.whn_step3" },
              { n: "4", Icon: AlarmClock,    tKey: "home.whn_step4", personalized: nextEvent ? { label: nextEvent.label, date: nextDateFmt } : null },
              { n: "5", Icon: ClipboardCheck,tKey: "home.whn_step5" },
              { n: "6", Icon: ShieldCheck,   tKey: "home.whn_step6" },
            ].map(({ n, Icon, tKey, personalized }) => (
              <li
                key={n}
                data-testid={`whn-step-${n}`}
                className={`relative bg-white/10 hover:bg-white/15 backdrop-blur-sm rounded-2xl p-4 border transition-colors ${
                  personalized ? "border-emerald-300/60 ring-2 ring-emerald-300/30" : "border-white/15"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">Step {n}</div>
                    <div className="font-bold leading-tight">{t(tKey)}</div>
                  </div>
                </div>
                {personalized ? (
                  <div className="mt-2" data-testid={`whn-step-${n}-personal`}>
                    <div className="inline-flex items-center gap-1.5 bg-emerald-400/25 border border-emerald-300/40 text-white px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest">
                      <AlarmClock className="w-3 h-3" /> {personalized.date}
                    </div>
                    <div className="mt-1.5 text-sm text-white font-semibold leading-snug">{personalized.label}</div>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-white/80 leading-snug">{t(`${tKey}_d`)}</p>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Continue where you left off — returning signed-in users only */}
      {user && typeof user === "object" && recent.length > 0 && (
        <section
          className="rounded-2xl bg-[#FAF5EC] border-2 border-[#E4DDD3] p-5 sm:p-6"
          data-testid="continue-card"
        >
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-[#C85A32]" />
            <h2 className="text-lg sm:text-xl font-extrabold text-[#1C2024]">
              {lang === "es" ? "Continúa donde lo dejaste" : "Continue where you left off"}
            </h2>
          </div>
          <p className="text-sm text-[#4A525A] mt-1">
            {lang === "es"
              ? "Retomas tu trabajo más reciente con un toque."
              : "Pick up your most recent work in one tap."}
          </p>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {recent.map((r) => {
              const meta = ({
                case: { Icon: ClipboardList, kicker: lang === "es" ? "Mi caso" : "My case" },
                learn: { Icon: GraduationCap, kicker: lang === "es" ? "Aprender" : "Learn" },
                builder: { Icon: FileEdit, kicker: lang === "es" ? "Borrador" : "Draft" },
                research: { Icon: Scale, kicker: lang === "es" ? "Investigación" : "Research" },
              })[r.kind] || { Icon: History, kicker: r.kind };
              const Icon = meta.Icon;
              return (
                <li key={r.kind}>
                  <Link
                    to={r.href}
                    data-testid={`continue-item-${r.kind}`}
                    className="pod-focus block bg-white hover:bg-[#FBF4EB] border-2 border-[#E4DDD3] hover:border-[#C85A32] rounded-xl p-4 transition-colors h-full"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#FAF5EC] text-[#C85A32] flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-[#707880] font-extrabold">{meta.kicker}</div>
                        <div className="text-sm font-bold text-[#1C2024] truncate">{r.title}</div>
                        <div className="text-xs text-[#4A525A] truncate mt-0.5">{r.subtitle}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#C85A32] shrink-0" />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">{t("home.section_title")}</h2>
        <p className="text-[#4A525A] mb-6">{t("home.section_sub")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {tasks.map((task) => {
            const Icon = task.Icon;
            return (
              <Link
                key={task.testid}
                to={task.to}
                data-testid={task.testid}
                className="pod-focus group bg-white hover:bg-[#FAF5EC] border-2 border-[#E4DDD3] hover:border-[#C85A32] rounded-2xl p-5 sm:p-6 text-left shadow-sm hover:shadow-md transition-all flex flex-col justify-between h-full"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FAF5EC] group-hover:bg-[#C85A32] text-[#C85A32] group-hover:text-white flex items-center justify-center transition-colors mb-4">
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-lg font-bold text-[#1C2024]">{task.displayLabel}</div>
                  <div className="text-sm text-[#4A525A] mt-1 leading-relaxed">{task.displayDesc}</div>
                </div>
                <div className="mt-4 text-sm font-semibold text-[#C85A32] inline-flex items-center gap-1">
                  {t("home.open")} <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/paths" data-testid="shortcut-paths" className="pod-focus bg-[#2C4A3E] hover:bg-[#1F382E] text-white p-5 rounded-2xl transition-colors">
          <div className="text-xs uppercase tracking-widest opacity-80 font-semibold">{t("home.framework")}</div>
          <div className="text-xl font-bold mt-1">{t("home.paths_short")}</div>
          <div className="text-sm opacity-85 mt-1">{t("home.paths_desc")}</div>
        </Link>
        <Link to="/roles" data-testid="shortcut-roles" className="pod-focus bg-[#FAF5EC] hover:bg-[#F3EFE6] border-2 border-[#E4DDD3] p-5 rounded-2xl transition-colors">
          <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{t("home.who_is_who")}</div>
          <div className="text-xl font-bold mt-1 text-[#1C2024]">{t("home.roles_short")}</div>
          <div className="text-sm text-[#4A525A] mt-1">{t("home.roles_desc")}</div>
        </Link>
        <Link to="/what-if" data-testid="shortcut-what-if" className="pod-focus bg-white hover:bg-[#FAF5EC] border-2 border-[#E4DDD3] p-5 rounded-2xl transition-colors">
          <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{t("home.decision_center")}</div>
          <div className="text-xl font-bold mt-1 text-[#1C2024]">{t("home.whatif_short")}</div>
          <div className="text-sm text-[#4A525A] mt-1">{t("home.whatif_desc")}</div>
        </Link>
      </section>

      <p className="text-xs text-[#707880] italic text-center mt-2" data-testid="legal-info-notice">
        {t("home.legal_notice")}
      </p>

      {/* Mobile-only sticky CTA — thumb-reach primary action */}
      <Link
        to="/summarize"
        data-testid="mobile-sticky-cta"
        className="pod-focus md:hidden fixed left-3 right-3 bottom-[76px] z-40 bg-[#C85A32] hover:bg-[#B24C28] text-white font-extrabold py-3.5 px-5 rounded-2xl shadow-2xl inline-flex items-center justify-center gap-2 text-base"
      >
        <FileText className="w-5 h-5" />
        {lang === "es" ? "Explicar un documento" : "Explain a document"}
        <ArrowRight className="w-5 h-5" />
      </Link>
    </div>
  );
}
